// The report card: one trading week (Sunday to Friday, by futures trading day) or one calendar
// month graded A+ to F in six subjects, with blunt comments and assignments. Every grade
// comes from the trader's own trades and journal; the wording is deliberately direct.
import { formatMoney, netPnl, tradingDayKey, usd } from './pnl';
import { tagKind } from './tags';
import { rootSymbol } from './tools/contracts';
import { translateNow as tn } from './langState';
import { improvePlan } from './improve';

export const MIN_WEEK_TRADES = 5;
export const MIN_MONTH_TRADES = 10;
export const MIN_QUARTER_TRADES = 30;
// Process counts more than profit: a green week can still be a badly traded one.
const SUBJECT_WEIGHTS = { results: 15, risk: 25, discipline: 20, emotions: 20, journaling: 10, focus: 10 };

const LETTERS = [[97, 'A+'], [93, 'A'], [90, 'A-'], [87, 'B+'], [83, 'B'], [80, 'B-'], [77, 'C+'], [73, 'C'], [70, 'C-'], [67, 'D+'], [63, 'D'], [60, 'D-']];
export function letter(score) {
  const hit = LETTERS.find(([min]) => score >= min);
  return hit ? hit[1] : 'F';
}

const clamp = (n) => Math.max(0, Math.min(100, Math.round(n)));
const pct = (n) => `${Math.round(n)}%`;
const tradesText = (n) => tn(n === 1 ? '{n} trade' : '{n} trades', { n });
const daysText = (n) => tn(n === 1 ? '{n} day' : '{n} days', { n });

// The Sunday that starts a trading day's week, as YYYY-MM-DD.
export function weekOf(dayKey) {
  const d = new Date(`${dayKey}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d.toISOString().slice(0, 10);
}

export function addDays(dayKey, n) {
  const d = new Date(`${dayKey}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Weeks that have trades, newest first.
export function tradedWeeks(trades) {
  return [...new Set(trades.map((t) => weekOf(tradingDayKey(t.date))))].sort().reverse();
}

// The month a trading day belongs to, as its first day (YYYY-MM-01), and the month before.
export const monthOf = (dayKey) => `${dayKey.slice(0, 7)}-01`;
export function previousMonth(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, '0')}-01`;
}
// The quarter a trading day belongs to, as its first day (YYYY-01-01, -04-01, -07-01, -10-01).
export const quarterOf = (dayKey) => `${dayKey.slice(0, 4)}-${String(Math.floor((Number(dayKey.slice(5, 7)) - 1) / 3) * 3 + 1).padStart(2, '0')}-01`;
export function previousQuarter(quarterKey) {
  const [y, m] = quarterKey.split('-').map(Number);
  return m === 1 ? `${y - 1}-10-01` : `${y}-${String(m - 3).padStart(2, '0')}-01`;
}
export const periodOf = (dayKey, period) => (period === 'quarter' ? quarterOf(dayKey) : period === 'month' ? monthOf(dayKey) : weekOf(dayKey));
export const minTradesFor = (period) => (period === 'quarter' ? MIN_QUARTER_TRADES : period === 'month' ? MIN_MONTH_TRADES : MIN_WEEK_TRADES);

// A quarter's wording is the month's with the word swapped (English, and Spanish, where "mes" and
// "trimestre" are both masculine so the sentence still agrees). `x` is a string or a list of them.
const SWAPS = [[/\bMonthly\b/g, 'Quarterly'], [/\bmonthly\b/g, 'quarterly'], [/\bMonths\b/g, 'Quarters'], [/\bmonths\b/g, 'quarters'], [/\bMonth\b/g, 'Quarter'], [/\bmonth\b/g, 'quarter'],
  [/\bMensual\b/g, 'Trimestral'], [/\bmensual\b/g, 'trimestral'], [/\bMeses\b/g, 'Trimestres'], [/\bmeses\b/g, 'trimestres'], [/\bMes\b/g, 'Trimestre'], [/\bmes\b/g, 'trimestre']];
export function asQuarter(x) {
  if (typeof x === 'string') return SWAPS.reduce((text, [from, to]) => text.replace(from, to), x);
  return Array.isArray(x) ? x.map(asQuarter) : x;
}

// Weeks or months that have trades, newest first.
export function tradedPeriods(trades, period) {
  return [...new Set(trades.map((t) => periodOf(tradingDayKey(t.date), period)))].sort().reverse();
}

const prepDone = (n) => Boolean(n && ((n.morningDone && n.morningDone.length) || (n.moodBefore && n.moodBefore.length) || (n.plan && n.plan.trim())));
const reflectionDone = (n) => Boolean(n && (n.score || (n.review && n.review.trim()) || (n.eveningDone && n.eveningDone.length)));

// Micro and full-size contracts are the same market.
const MARKET = { MNQ: 'NQ', NQ: 'NQ', NNQ: 'NQ', MES: 'ES', ES: 'ES', NES: 'ES', MYM: 'YM', YM: 'YM', M2K: 'RTY', RTY: 'RTY',
  MGC: 'GC', GC: 'GC', MCL: 'CL', CL: 'CL', SIL: 'SI', SI: 'SI' };
const MARKET_NAME = { NQ: 'Nasdaq', ES: 'S&P 500', YM: 'Dow', RTY: 'Russell 2000', GC: 'Gold', CL: 'Crude oil', SI: 'Silver' };
const marketOf = (t) => MARKET[rootSymbol(t.symbol)] || rootSymbol(t.symbol);
const marketName = (m) => (MARKET_NAME[m] ? tn(MARKET_NAME[m]) : m);

// The numbers behind a week's grades.
function weekStats(trades, { notes, rules, library }) {
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  const nets = sorted.map(netPnl);
  const wins = nets.filter((n) => n > 0);
  const losses = nets.filter((n) => n < 0);
  const grossWin = wins.reduce((a, b) => a + b, 0);
  const grossLoss = -losses.reduce((a, b) => a + b, 0);
  const byDay = new Map();
  sorted.forEach((t, i) => {
    const k = tradingDayKey(t.date);
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k).push({ t, net: nets[i] });
  });
  const dayNets = [...byDay.entries()].map(([day, list]) => ({ day, net: list.reduce((s, x) => s + x.net, 0) }));

  // Losses far bigger than usual: more than 1.5x the typical (median) loss.
  const sortedLosses = [...losses].sort((a, b) => a - b);
  const typicalLoss = sortedLosses.length ? sortedLosses[Math.floor(sortedLosses.length / 2)] : 0;
  const outsized = sortedLosses.filter((l) => l < typicalLoss * 1.5);
  // The same losses as trades, for "How to improve" (biggest first).
  const outsizedTrades = sorted.map((t, i) => ({ t, net: nets[i] })).filter((x) => x.net < 0 && x.net < typicalLoss * 1.5).sort((a, b) => a.net - b.net);

  // Trades taken after 2 or more losses earlier the same day (on any account).
  const afterTwo = [];
  for (const list of byDay.values()) {
    let lost = 0;
    for (const { t, net } of list) {
      if (lost >= 2) afterTwo.push({ t, net });
      if (net < 0) lost += 1;
    }
  }

  const isMistake = (t) => (t.tags || []).some((tag) => tagKind(tag, library) === 'mistake');
  const mistakes = sorted.filter(isMistake);
  const days = [...byDay.keys()];
  const checkedDays = days.filter((d) => notes[d] && notes[d].rulesFollowed && notes[d].rulesFollowed.length);
  const allRulesDays = rules.length ? checkedDays.filter((d) => rules.every((r) => notes[d].rulesFollowed.includes(r))) : [];
  const brokeDays = rules.length ? checkedDays.filter((d) => !allRulesDays.includes(d)) : [];
  const emotionDays = days.filter((d) => notes[d] && notes[d].emotionsHit && notes[d].emotionsHit.length);
  // How often each rule was broken on the days it was checked.
  const ruleBreaks = rules.map((r) => ({ rule: r, days: checkedDays.filter((d) => !notes[d].rulesFollowed.includes(r)).length })).filter((x) => x.days).sort((a, b) => b.days - a.days);
  // What each mistake tag cost.
  const mistakeTags = new Map();
  for (const t of mistakes) {
    for (const tag of (t.tags || []).filter((x) => tagKind(x, library) === 'mistake')) {
      const m = mistakeTags.get(tag) || { tag, count: 0, net: 0 };
      m.count += 1; m.net += netPnl(t);
      mistakeTags.set(tag, m);
    }
  }
  const emotionCounts = new Map();
  for (const d of emotionDays) for (const e of notes[d].emotionsHit) emotionCounts.set(e, (emotionCounts.get(e) || 0) + 1);

  const markets = new Map();
  for (const t of sorted) {
    const m = marketOf(t);
    markets.set(m, (markets.get(m) || []).concat(t));
  }

  return {
    count: sorted.length, net: nets.reduce((a, b) => a + b, 0), grossWin, grossLoss,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : 0,
    avgWin: wins.length ? grossWin / wins.length : 0,
    avgLoss: losses.length ? grossLoss / losses.length : 0,
    winRate: sorted.length ? (wins.length / sorted.length) * 100 : 0,
    bestDay: [...dayNets].sort((a, b) => b.net - a.net)[0],
    outsizedTrades,
    dayCounts: Object.fromEntries([...byDay.entries()].map(([d, list]) => [d, list.length])),
    ruleBreaks,
    mistakeTags: [...mistakeTags.values()].sort((a, b) => a.net - b.net),
    emotionCounts: [...emotionCounts.entries()].sort((a, b) => b[1] - a[1]),
    untagged: sorted.filter((t) => !(t.tags || []).some((tag) => tagKind(tag, library) === 'setup')).length,
    noPrepDays: days.filter((d) => !prepDone(notes[d])),
    noReflectDays: days.filter((d) => !reflectionDone(notes[d])),
    days, dayNets, greenDays: dayNets.filter((d) => d.net > 0).length,
    biggestLoss: sortedLosses.length ? sortedLosses[0] : 0, typicalLoss, outsized, lossCount: sortedLosses.length,
    worstDay: [...dayNets].sort((a, b) => a.net - b.net)[0],
    afterTwo, afterTwoNet: afterTwo.reduce((s, x) => s + x.net, 0), afterTwoLoss: afterTwo.reduce((s, x) => s + Math.min(0, x.net), 0),
    mistakes, mistakeNet: mistakes.reduce((s, t) => s + netPnl(t), 0),
    checkedDays, allRulesDays, brokeDays, brokeNet: dayNets.filter((d) => brokeDays.includes(d.day)).reduce((s, d) => s + d.net, 0),
    emotionDays,
    tagged: sorted.filter((t) => (t.tags || []).some((tag) => tagKind(tag, library) === 'setup')).length,
    prepDays: days.filter((d) => prepDone(notes[d])).length,
    reflectDays: days.filter((d) => reflectionDone(notes[d])).length,
    markets: [...markets.entries()].map(([m, list]) => ({ market: m, count: list.length, net: list.reduce((s, t) => s + netPnl(t), 0) })),
    accounts: new Set(sorted.map((t) => String(t.accountId || ''))).size,
  };
}

// Each subject: a score (0-100), the facts in one line, a blunt comment and an assignment.
function gradeResults(s, noJournal, c) {
  const pf = s.profitFactor;
  let score = pf >= 2.5 ? 97 : pf >= 2 ? 92 : pf >= 1.5 ? 86 : pf >= 1.2 ? 79 : pf >= 1 ? 72 : pf >= 0.8 ? 62 : pf >= 0.5 ? 52 : 40;
  if (s.net < 0) score = Math.min(score, 66);
  const pfText = pf === Infinity ? '∞' : pf.toFixed(2);
  const facts = tn('{net} over {trades} · profit factor {pf} · {green} of {days} days green',
    { net: formatMoney(s.net), trades: tradesText(s.count), pf: pfText, green: s.greenDays, days: s.days.length });
  let comment;
  if (s.net < 0) {
    comment = pf > 0
      ? c.w('You lost {amount} this week. Every $1 you made cost you {cost} to make. That isn’t a rough patch; that’s a week your process paid the market.',
        'You lost {amount} this month. Every $1 you made cost you {cost} to make. That isn’t a rough patch; that’s a month your process paid the market.', { amount: usd.format(-s.net), cost: usd.format(1 / pf) })
      : c.w('You lost {amount} this week without a single winning trade.', 'You lost {amount} this month without a single winning trade.', { amount: usd.format(-s.net) });
  } else if (pf >= 1.5) {
    comment = c.w('A profitable week: {net} with a profit factor of {pf}. Don’t change what’s working. Your job next week is to protect it.',
      'A profitable month: {net} with a profit factor of {pf}. Don’t change what’s working. Your job next month is to protect it.', { net: formatMoney(s.net), pf: pfText });
  } else {
    comment = c.w('Green, but thin: {net} with a profit factor of {pf}. One more bad trade and this week was red.',
      'Green, but thin: {net} with a profit factor of {pf}. A couple more bad trades and this month was red.', { net: formatMoney(s.net), pf: pfText });
  }
  return { score: clamp(score), facts, comment, tip: c.w('Trade only your best setup next week, at your normal size, and let the green days carry the week.',
    'Trade only your best setup next month, at your normal size, and let the green days carry the month.') };
}

function gradeRisk(s, noJournal, c) {
  if (!s.biggestLoss) {
    return { score: 95, facts: c.w('No losing trades this week', 'No losing trades this month'),
      comment: c.w('No losses to judge. Enjoy it, and keep the same stops next week.', 'No losses to judge. Enjoy it, and keep the same stops next month.'), tip: null };
  }
  const ratio = s.typicalLoss ? s.biggestLoss / s.typicalLoss : 1;
  const winsErased = s.avgWin ? Math.round(-s.biggestLoss / s.avgWin) : 0;
  // Each outsized loss costs more the bigger it is: 1.6x typical is a slip, 5x is a stop that wasn't there.
  // Over a month the penalty is per week traded, so a month isn't graded four times as hard.
  let score = 95 - s.outsized.reduce((sum, l) => sum + Math.min(25, 8 + 4 * (l / s.typicalLoss - 1.5)), 0) / c.scale;
  const worstShare = s.grossLoss ? -s.worstDay.net / s.grossLoss : 0;
  if (s.worstDay && s.worstDay.net < 0 && worstShare > 0.5) score -= 10;
  const facts = tn(s.outsized.length === 1 ? 'Biggest loss {big} · typical loss {typical} · 1 loss over 1.5× typical' : 'Biggest loss {big} · typical loss {typical} · {n} losses over 1.5× typical',
    { big: formatMoney(s.biggestLoss), typical: formatMoney(s.typicalLoss), n: s.outsized.length });
  const erased = winsErased >= 2 ? tn(' and wiped out about {n} average wins', { n: winsErased }) : '';
  let comment;
  if (s.outsized.length >= 2) {
    comment = tn('{n} of your losses were far bigger than usual, the worst {ratio}× your typical one{erased}. That’s not a strategy problem; it’s stops you didn’t respect.',
      { n: s.outsized.length, ratio: ratio.toFixed(1), erased });
  } else if (s.outsized.length === 1) {
    comment = tn('One loss was {ratio}× your typical one{erased}. Find out why that stop didn’t hold before it happens twice.', { ratio: ratio.toFixed(1), erased });
  } else {
    comment = tn('Your losses stayed close to your plan size. That’s what keeps an account alive long enough to get good.');
  }
  return { score: clamp(score), facts, comment,
    tip: tn('Every trade gets a stop in the platform before you click buy or sell. No exceptions, no moving it.') };
}

function gradeDiscipline(s, noJournal, c) {
  const mistakeShare = s.count ? s.mistakes.length / s.count : 0;
  const ruleRate = s.checkedDays.length ? s.allRulesDays.length / s.checkedDays.length : null;
  const score = ruleRate === null ? 95 - mistakeShare * 120 : (ruleRate * 100) * 0.55 + (100 - mistakeShare * 120) * 0.45;
  const facts = ruleRate === null
    ? tn('{n} of {trades} trades tagged as mistakes', { n: s.mistakes.length, trades: s.count })
    : tn('All rules followed on {ok} of {days} days · {n} mistake trades', { ok: s.allRulesDays.length, days: s.checkedDays.length, n: s.mistakes.length });
  let comment;
  if (s.brokeDays.length && s.brokeNet < 0) {
    comment = tn('You broke your own rules on {days}, and those days cost you {amount}. You wrote the rules for a reason.', { days: daysText(s.brokeDays.length), amount: formatMoney(s.brokeNet) });
  } else if (s.mistakes.length && s.mistakeNet < 0) {
    comment = tn('{n} trades you tagged as mistakes cost {amount}. You already know which trades those are; the fix is not taking them.', { n: s.mistakes.length, amount: formatMoney(s.mistakeNet) });
  } else if (s.mistakes.length) {
    comment = c.w('{n} mistake trades this week, even if some worked out. Winning mistakes are the dangerous ones: they teach you to repeat them.',
      '{n} mistake trades this month, even if some worked out. Winning mistakes are the dangerous ones: they teach you to repeat them.', { n: s.mistakes.length });
  } else {
    comment = tn('No mistake trades and your rules held. This is the standard; keep it there.');
  }
  return { score: clamp(score), facts, comment, tip: tn('Read your rules out loud before the open, and end the day the first time you break one.') };
}

// With `noJournal` (the free report card, a trade file only) this is "Tilt control": it can
// only see what the trades did after losses, not how the trader felt.
function gradeEmotions(s, noJournal, c) {
  // Trading on after 2 losses costs points; losing real money doing it (more than two typical
  // losses, even if a win later covered it) costs more.
  const costly = s.afterTwoNet < s.typicalLoss;
  const heavy = s.afterTwoLoss < s.typicalLoss * 2;
  const score = 95 - Math.min(30, (4 * s.afterTwo.length) / c.scale) - (costly || heavy ? 15 : s.afterTwoNet < 0 ? 5 : 0) - (8 * s.emotionDays.length) / c.scale;
  const facts = noJournal
    ? tn('{n} trades after 2 losses in a day ({amount})', { n: s.afterTwo.length, amount: formatMoney(s.afterTwoNet) })
    : tn('{n} trades after 2 losses in a day ({amount}) · emotions hit on {days}',
      { n: s.afterTwo.length, amount: formatMoney(s.afterTwoNet), days: daysText(s.emotionDays.length) });
  const comment = s.afterTwo.length && costly
    ? tn('After 2 losses you kept going {n} more times and lost another {amount}. Those losses came from the decision to keep going, not from the market.', { n: s.afterTwo.length, amount: usd.format(-s.afterTwoNet) })
    : s.afterTwo.length && heavy
      ? tn('After 2 losses you kept trading {n} more times and took {losses} in losses doing it. A win bailed you out this time ({amount}); next time it won’t.',
        { n: s.afterTwo.length, losses: formatMoney(s.afterTwoLoss), amount: formatMoney(s.afterTwoNet) })
      : s.afterTwo.length
        ? tn('After 2 losses you kept trading {n} more times. It came out at {amount} this time. That’s luck, not a plan.', { n: s.afterTwo.length, amount: formatMoney(s.afterTwoNet) })
      : s.emotionDays.length && !noJournal
      ? c.w('Emotions got into {days} this week. Name them in your reflection, then plan what you’ll do differently when they show up.',
        'Emotions got into {days} this month. Name them in your reflection, then plan what you’ll do differently when they show up.', { days: daysText(s.emotionDays.length) })
      : tn('You stopped when you should have. That’s rarer than a good entry.');
  return { score: clamp(score), facts, comment, tip: tn('Two losses in a day ends the day, on every account. Close the platform and write your review.') };
}

function gradeJournaling(s) {
  const d = s.days.length || 1;
  const taggedShare = s.count ? s.tagged / s.count : 0;
  const score = (taggedShare * 0.4 + (s.prepDays / d) * 0.3 + (s.reflectDays / d) * 0.3) * 100;
  const facts = tn('{tagged} of {trades} trades tagged with a setup · prep on {prep}/{days} days · reflection on {reflect}/{days}',
    { tagged: s.tagged, trades: s.count, prep: s.prepDays, reflect: s.reflectDays, days: s.days.length });
  const gaps = [
    s.tagged < s.count && tn('{missing} of your trades have no setup tag', { missing: pct((1 - taggedShare) * 100) }),
    s.prepDays < s.days.length && tn('{n} of {days} trading days had no morning prep', { n: s.days.length - s.prepDays, days: s.days.length }),
    s.reflectDays < s.days.length && tn('{n} of {days} had no reflection', { n: s.days.length - s.reflectDays, days: s.days.length }),
  ].filter(Boolean);
  const comment = score >= 85 || !gaps.length
    ? tn('Your journal is doing its job. The data behind every other grade on this card comes from it.')
    : tn('You can’t fix what you don’t record: {gaps}.', { gaps: gaps.join(tn('; ')) });
  return { score: clamp(score), facts, comment, tip: tn('Tag every trade and write two lines of reflection the same day, before you look at P&L.') };
}

function gradeFocus(s, noJournal, c) {
  const solid = s.markets.filter((m) => m.count >= 3);
  const best = [...solid].sort((a, b) => b.net - a.net)[0];
  // Losing markets other than the best one (or every losing market, when even the best lost).
  const losing = solid.filter((m) => m.net < 0 && (m !== best || best.net < 0));
  const allLost = solid.length >= 2 && losing.length === solid.length;
  const score = 95 - 15 * losing.length - (s.markets.length > 2 ? 8 : 0) - (s.accounts > 2 ? 5 : 0);
  const facts = `${s.markets.length === 1 ? tn('1 market') : tn('{n} markets', { n: s.markets.length })} · ${s.accounts === 1 ? tn('1 account') : tn('{n} accounts', { n: s.accounts })}`;
  const names = (list) => list.map((m) => marketName(m.market)).join(', ');
  let comment;
  if (allLost) {
    comment = c.w('Every market you traded lost money this week ({markets}). More markets didn’t spread the risk; they multiplied it.',
      'Every market you traded lost money this month ({markets}). More markets didn’t spread the risk; they multiplied it.', { markets: names(losing) });
  } else if (losing.length && best && best.net > 0) {
    comment = tn('{best} made {bestAmount}; {losing} lost {lostAmount}. You’re not diversifying, you’re diluting. Your edge is in one market.',
      { best: marketName(best.market), bestAmount: formatMoney(best.net), losing: names(losing), lostAmount: formatMoney(losing.reduce((sum, m) => sum + m.net, 0)) });
  } else if (s.markets.length <= 1) {
    comment = tn('One market, full attention. That’s how you get good at something.');
  } else {
    comment = c.w('Several markets this week, but none of them is clearly hurting you yet. Watch it.', 'Several markets this month, but none of them is clearly hurting you yet. Watch it.');
  }
  return { score: clamp(score), facts, comment,
    tip: best && (losing.length || s.markets.length > 1)
      ? c.w('Trade only {market} next week. Everything else waits until you’ve had a green week there.',
        'Trade only {market} next month. Everything else waits until you’ve had a green month there.', { market: marketName(best.market) }) : null };
}

const SUBJECTS = [
  ['results', 'Results', gradeResults],
  ['risk', 'Risk control', gradeRisk],
  ['discipline', 'Discipline', gradeDiscipline],
  ['emotions', 'Emotional control', gradeEmotions],
  ['journaling', 'Journaling', gradeJournaling],
  ['focus', 'Focus', gradeFocus],
];

// What each grade looks at, for "How these grades work".
const MEASURES = {
  results: 'Net P&L and profit factor for the week. It counts the least: a green week can still be a badly traded one.',
  resultsMonth: 'Net P&L and profit factor for the month. It counts the least: a green month can still be a badly traded one.',
  risk: 'How big your losses were compared with your typical loss. One loss far bigger than the rest is a stop that didn’t hold.',
  discipline: 'Days you followed all your rules, and trades you tagged as mistakes.',
  emotions: 'Trades after 2 losses in a day, and days your check-in showed emotions like tilt or FOMO.',
  tilt: 'Trades you took after 2 losses in a day, and what they cost. It comes from your trades only, so it measures what you did, not how you felt.',
  journaling: 'Setup tags on your trades, morning prep and end-of-day reflections.',
  focus: 'Whether the markets you trade besides your best one make or lose money.',
};

// How many trades, losses or days a grade rests on, and when that's too few to trust yet.
function sampleOf(id, s) {
  if (id === 'risk') return { n: s.lossCount, text: tn(s.lossCount === 1 ? '{n} losing trade' : '{n} losing trades', { n: s.lossCount }), small: s.lossCount < 5 };
  if (id === 'discipline' || id === 'journaling') return { n: s.days.length, text: daysText(s.days.length), small: s.days.length < 3 };
  if (id === 'emotions') return { n: s.days.length, text: tn('{n} trading days', { n: s.days.length }), small: s.days.length < 3 };
  return { n: s.count, text: tradesText(s.count), small: s.count < 10 };
}

// Subjects a trade file alone can grade (no journal): for the free report card.
export const TRADE_ONLY_SUBJECTS = ['results', 'risk', 'emotions', 'focus'];

// An improvement plan in quarter words: only its own text (steps and target), never the trades
// and notes it quotes as evidence.
const quarterPlan = (plan, quarter) => (plan && quarter ? { ...plan, steps: asQuarter(plan.steps), target: asQuarter(plan.target) } : plan);

function gradeWeek(trades, data) {
  // A quarter is graded like a month (counts per week traded), in quarter words.
  const quarter = data.period === 'quarter';
  const month = data.period === 'month' || quarter;
  const words = (text) => (quarter ? asQuarter(text) : text);
  if (trades.length < minTradesFor(data.period)) return null;
  const s = weekStats(trades, data);
  const noJournal = Boolean(data.noJournal);
  // Week or month wording, and how many weeks a month's counts are spread over.
  const c = {
    w: (weekText, monthText, vars) => words(tn(month ? monthText : weekText, vars)),
    scale: month ? Math.max(1, new Set(s.days.map(weekOf)).size) : 1,
  };
  const chosen = data.subjects ? SUBJECTS.filter(([id]) => data.subjects.includes(id)) : SUBJECTS;
  const weight = chosen.reduce((sum, [id]) => sum + SUBJECT_WEIGHTS[id], 0);
  const subjects = chosen.map(([id, label, grade]) => {
    const g = grade(s, noJournal, c);
    const tilt = id === 'emotions' && noJournal;
    return {
      id, label: tilt ? tn('Tilt control') : tn(label), ...g, grade: letter(g.score),
      measures: words(tn(MEASURES[tilt ? 'tilt' : id === 'results' && month ? 'resultsMonth' : id])), weight: Math.round((SUBJECT_WEIGHTS[id] / weight) * 100), sample: sampleOf(id, s),
      improve: quarterPlan(improvePlan(id, s, { month, rules: data.rules || [], noJournal, marketName }), quarter),
    };
  });
  const weighted = subjects.reduce((sum, x) => sum + x.score * SUBJECT_WEIGHTS[x.id], 0) / weight;
  // How the week was traded, without the P&L. A profitable week can't grade much better than it.
  const process = subjects.filter((x) => x.id !== 'results');
  const processWeight = process.reduce((sum, x) => sum + SUBJECT_WEIGHTS[x.id], 0);
  const processScore = processWeight ? process.reduce((sum, x) => sum + x.score * SUBJECT_WEIGHTS[x.id], 0) / processWeight : weighted;
  const score = clamp(Math.min(weighted, processScore + 5));
  const results = subjects.find((x) => x.id === 'results');
  return {
    stats: s, subjects, score, grade: letter(score),
    // Green, but traded badly: the money doesn't excuse the process.
    sloppyWin: Boolean(results && results.score >= 79 && s.net > 0 && processScore < 73),
  };
}

// The report card for one week (`weekKey` = its Sunday) or, with `data.period: 'month'`, one
// month (`weekKey` = its first day), compared with the one before.
// `data` = { notes, rules, library, subjects?, noJournal?, period? } (subjects: which ids to grade).
export function reportCard(allTrades, weekKey, data) {
  const period = ['month', 'quarter'].includes(data.period) ? data.period : 'week';
  const inWeek = (key) => allTrades.filter((t) => periodOf(tradingDayKey(t.date), period) === key);
  const trades = inWeek(weekKey);
  const current = gradeWeek(trades, data);
  if (!current) return { weekKey, period, count: trades.length, tooFew: true };
  const previous = gradeWeek(inWeek(period === 'quarter' ? previousQuarter(weekKey) : period === 'month' ? previousMonth(weekKey) : addDays(weekKey, -7)), data);
  const subjects = current.subjects.map((x) => {
    const before = previous && previous.subjects.find((p) => p.id === x.id);
    return { ...x, change: before ? x.score - before.score : null, previousGrade: before ? before.grade : null };
  });
  const ranked = [...subjects].sort((a, b) => a.score - b.score);
  // The weakest subject below a B is this week's one thing to fix; the next two follow it.
  const fixFirst = ranked[0].score < 83 && ranked[0].tip ? ranked[0] : null;
  return {
    weekKey,
    period,
    count: trades.length,
    net: current.stats.net,
    score: current.score,
    grade: current.grade,
    sloppyWin: current.sloppyWin,
    change: previous ? current.score - previous.score : null,
    previousGrade: previous ? previous.grade : null,
    subjects,
    fixFirst,
    assignments: ranked.filter((x) => x !== fixFirst && x.score < 83 && x.tip).slice(0, 2).map((x) => ({ id: x.id, label: x.label, tip: x.tip })),
  };
}
